import { Field } from "react-final-form";

import { MENTION_SOURCE_TYPE } from "@/constants";
import { useCapabilities } from "@/contexts/CapabilitiesContext";
import {
  keywordMinLengthWithoutWildcard,
  keywordMinLengthWithWildcard,
  sequenceValidators,
  toArrayValidator,
  validateRequiredArray,
} from "@/utils";
import { LanguageDisplay, SourceDisplay } from "@components/DataDisplay";
import {
  FF_Checkbox,
  FF_SelectField,
  FF_TokenizedInputField,
} from "@components/Field";
import Label from "@components/Field/Label";

import { WORDS_APART_OPTIONS } from "../settings";
import { useLingui } from "@lingui/react/macro";

export type SearchQueryProps = {
  name: string;
  advanced: boolean;
};

const SearchQuery: React.FC<SearchQueryProps> = ({ name, advanced }) => {
  const { t } = useLingui();

  // Validators for keywords
  const keywordValidators = sequenceValidators(
    keywordMinLengthWithWildcard(
      t`Sökord med '*' måste bestå av minst 3 tecken`
    ),
    keywordMinLengthWithoutWildcard(t`Sökord måste bestå av minst 2 tecken`)
  );

  // Validators for keywords that works with FF. That is, works on the full array of values

  const FF_validators = toArrayValidator(
    sequenceValidators(
      keywordMinLengthWithWildcard(
        t`Sökord med '*' måste bestå av minst 3 tecken`
      ),
      keywordMinLengthWithoutWildcard(t`Sökord måste bestå av minst 2 tecken`)
    )
  );

  const FF_required_validators = sequenceValidators(
    validateRequiredArray(t`Minst ett sökord krävs`),
    FF_validators
  );

  const { capabilities } = useCapabilities();

  const TYPE_OPTIONS = [
    { value: MENTION_SOURCE_TYPE.SPEECH, label: t`Endast träffar i tal` },
    { value: MENTION_SOURCE_TYPE.TEXT, label: t`Endast träffar i text` },
    { value: MENTION_SOURCE_TYPE.ALL, label: t`Alla träffar` },
  ];

  return (
    <>
      <div className="flex flex-col gap-2">
        <Field
          block
          required
          name={`${name}.keywords`}
          data-testid="input-keywords"
          label={L.EXPLORE_KEYWORD_LABEL}
          placeholder={L.EXPLORE_KEYWORD_PLACEHOLDER}
          helperText={L.EXPLORE_KEYWORD_NEAR_HELP}
          component={FF_TokenizedInputField}
          validateItem={keywordValidators}
          validate={FF_required_validators}
        />

        <div>
          <Field
            block
            name={`${name}.near_keywords`}
            data-testid="input-near-keywords"
            label={L.EXPLORE_KEYWORD_NEAR_LABEL}
            component={FF_TokenizedInputField}
            validateItem={keywordValidators}
            placeholder={L.EXPLORE_KEYWORD_NEAR_PLACEHOLDER}
          />
          {advanced && (
            <Field
              name={`${name}.near_distance`}
              defaultValue={10}
              component={FF_SelectField}
              label={L.EXPLORE_KEYWORD_NEAR_MAX_NEAR_LABEL}
              options={WORDS_APART_OPTIONS}
            />
          )}
        </div>
        {advanced && (
          <>
            <div>
              <Field
                block
                name={`${name}.not_near_keywords`}
                data-testid="input-exclude-keywords"
                label={L.EXPLORE_KEYWORD_EXCLUDE}
                placeholder={L.EXPLORE_KEYWORD_EXCLUDE_PLACEHOLDER}
                component={FF_TokenizedInputField}
                validateItem={keywordValidators}
                validate={FF_validators}
              />
              <Field
                name={`${name}.not_near_distance`}
                label={L.EXPLORE_KEYWORD_NEAR_MAX_NOT_NEAR_LABEL}
                options={WORDS_APART_OPTIONS}
                component={FF_SelectField}
              />
            </div>

            <div>
              <Field
                block
                name={`${name}.include_channel_names`}
                data-testid="input-include-channel-names"
                label={L.EXPLORE_CHANNEL_INCLUDE}
                placeholder={L.EXPLORE_CHANNEL_INCLUDE_PLACEHOLDER}
                component={FF_TokenizedInputField}
              />
            </div>
            <div>
              <Field
                block
                name={`${name}.exclude_channel_names`}
                data-testid="input-exclude-channel-names"
                label={L.EXPLORE_CHANNEL_EXCLUDE}
                placeholder={L.EXPLORE_CHANNEL_EXCLUDE_PLACEHOLDER}
                component={FF_TokenizedInputField}
              />
            </div>

            <div>
              <Label content={L.EXPLORE_SOURCES} />
              <div className="flex items-center gap-y-1 gap-x-4 flex-wrap">
                {capabilities.sources_audio.map((value) => (
                  <Field
                    key={value}
                    name={`${name}.sources`}
                    data-testid="checkbox-sources"
                    label={<SourceDisplay id={value} />}
                    value={value}
                    type="checkbox"
                    component={FF_Checkbox}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label content={L.EXPLORE_LANGUAGES} />
              <div className="flex items-center gap-y-1 gap-x-4 flex-wrap">
                {capabilities.languages.map((value) => (
                  <Field
                    key={value}
                    name={`${name}.languages`}
                    data-testid="checkbox-languages"
                    label={<LanguageDisplay id={value} />}
                    value={value}
                    type="checkbox"
                    component={FF_Checkbox}
                  />
                ))}
              </div>
            </div>

            <div>
              <Field
                name={`${name}.mention_types`}
                data-testid="select-types"
                label={L.EXPLORE_CHANNEL_TYPE}
                options={TYPE_OPTIONS}
                component={FF_SelectField}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SearchQuery;
